import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Overline } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  QrCode,
  Copy,
  ExternalLink,
  Radio,
  Pencil,
  Archive,
  Trash2,
  X,
  Loader2,
  Power,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EVENT_TYPES = ["Wedding", "Reception", "Birthday", "Corporate", "Party", "Other"];
const FILTERS = [
  ["upcoming", "Upcoming"],
  ["active", "Active"],
  ["past", "Past"],
  ["archived", "Archived"],
];

const emptyForm = {
  name: "",
  event_type: "",
  date: "",
  start_time: "",
  end_time: "",
  assigned_dj: "",
  client_name: "",
  mode: "normal",
  max_requests_per_guest: 5,
  allow_messages: true,
};

const inputCls =
  "bg-white/5 border-white/10 h-11 text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-primary/50";

const todayStr = () => new Date().toISOString().slice(0, 10);
const bucket = (e) =>
  e.archived ? "archived" : e.date === todayStr() ? "active" : e.date > todayStr() ? "upcoming" : "past";

export default function EventsPanel({ token }) {
  const [events, setEvents] = useState(null);
  const [djs, setDjs] = useState([]);
  const [filter, setFilter] = useState("upcoming");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [qrEvent, setQrEvent] = useState(null);

  const auth = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    try {
      const [{ data: ev }, { data: dj }] = await Promise.all([
        axios.get(`${API}/events`, { headers: auth() }),
        axios.get(`${API}/djs`, { headers: auth() }),
      ]);
      setEvents(ev);
      setDjs(dj);
    } catch {
      toast.error("Could not load events.");
    }
  }, [auth]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (e) => {
    e.preventDefault();
    if (!form.event_type) {
      toast.error("Pick an event type.");
      return;
    }
    setSaving(true);
    try {
      const dj = djs.find((d) => d.email === form.assigned_dj);
      const payload = {
        ...form,
        max_requests_per_guest: Number(form.max_requests_per_guest) || 0,
        dj_name: dj ? dj.name : "",
      };
      if (editing) {
        await axios.patch(`${API}/events/${editing.id}`, payload, { headers: auth() });
        toast.success("Event updated.");
      } else {
        const { data } = await axios.post(`${API}/events`, payload, { headers: auth() });
        toast.success(`Event created. Guest code: ${data.code}`);
      }
      setShowForm(false);
      setEditing(null);
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save event.");
    } finally {
      setSaving(false);
    }
  };

  const patch = async (id, updates, msg) => {
    try {
      await axios.patch(`${API}/events/${id}`, updates, { headers: auth() });
      if (msg) toast.success(msg);
      load();
    } catch {
      toast.error("Could not update event.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this event and all its song requests? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/events/${id}`, { headers: auth() });
      toast.success("Event deleted.");
      load();
    } catch {
      toast.error("Could not delete event.");
    }
  };

  const copyLink = (code) => {
    const link = `${window.location.origin}/request/${code}`;
    const done = () => toast.success("Guest link copied!");
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(link).then(done).catch(() => {
        const ta = document.createElement("textarea");
        ta.value = link;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        done();
      });
    }
  };

  const startEdit = (e) => {
    setEditing(e);
    setForm({
      name: e.name,
      event_type: e.event_type,
      date: e.date,
      start_time: e.start_time || "",
      end_time: e.end_time || "",
      assigned_dj: e.assigned_dj || "",
      client_name: e.client_name || "",
      mode: e.mode || "normal",
      max_requests_per_guest: e.max_requests_per_guest ?? 5,
      allow_messages: e.allow_messages !== false,
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const visible = (events || []).filter((e) => bucket(e) === filter);
  const counts = Object.fromEntries(
    FILTERS.map(([id]) => [id, (events || []).filter((e) => bucket(e) === id).length])
  );

  return (
    <section data-testid="events-panel" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex gap-2 flex-wrap">
          {FILTERS.map(([id, label]) => (
            <button
              key={id}
              data-testid={`events-filter-${id}`}
              onClick={() => setFilter(id)}
              className={`rounded-full px-4 py-2 text-[11px] tracking-[0.2em] uppercase border transition-colors duration-300 ${
                filter === id
                  ? "bg-primary text-primary-foreground border-primary"
                  : "border-white/15 text-muted-foreground hover:border-primary/50 hover:text-primary"
              }`}
            >
              {label} ({counts[id]})
            </button>
          ))}
        </div>
        <button
          data-testid="create-event-btn"
          onClick={() => {
            setEditing(null);
            setForm(emptyForm);
            setShowForm(!showForm);
          }}
          className="rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-xs font-medium tracking-[0.2em] uppercase flex items-center gap-2 transition-transform duration-300 hover:scale-105"
        >
          {showForm ? <X size={14} /> : <Plus size={14} />}
          {showForm ? "Cancel" : "New Event"}
        </button>
      </div>

      {showForm && (
        <form
          data-testid="event-form"
          onSubmit={save}
          className="glass rounded-3xl p-6 sm:p-8 space-y-5"
        >
          <Overline>{editing ? `Editing: ${editing.name}` : "Create Event"}</Overline>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="ev-name">Event Name *</Label>
              <Input
                id="ev-name"
                data-testid="event-name-input"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Patel Wedding Reception"
                className={inputCls}
              />
            </div>
            <div className="space-y-2">
              <Label>Event Type *</Label>
              <Select
                value={form.event_type}
                onValueChange={(v) => setForm({ ...form, event_type: v })}
              >
                <SelectTrigger data-testid="event-type-select" className={inputCls}>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {EVENT_TYPES.map((t) => (
                    <SelectItem key={t} value={t} data-testid={`event-type-option-${t.toLowerCase()}`}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="ev-date">Event Date *</Label>
              <Input
                id="ev-date"
                type="date"
                data-testid="event-date-input"
                required
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                className={inputCls}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ev-start">Start Time</Label>
                <Input
                  id="ev-start"
                  type="time"
                  data-testid="event-start-input"
                  value={form.start_time}
                  onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                  className={inputCls}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ev-end">End Time</Label>
                <Input
                  id="ev-end"
                  type="time"
                  data-testid="event-end-input"
                  value={form.end_time}
                  onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                  className={inputCls}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Assigned DJ</Label>
              <Select
                value={form.assigned_dj || "none"}
                onValueChange={(v) => setForm({ ...form, assigned_dj: v === "none" ? "" : v })}
              >
                <SelectTrigger data-testid="event-dj-select" className={inputCls}>
                  <SelectValue placeholder="Unassigned" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none" data-testid="event-dj-option-none">Unassigned</SelectItem>
                  {djs.map((d) => (
                    <SelectItem
                      key={d.id}
                      value={d.email}
                      data-testid={`event-dj-option-${d.email.replace(/[^a-z0-9]+/g, "-")}`}
                    >
                      {d.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {djs.length === 0 && (
                <p className="text-[11px] text-muted-foreground/70">
                  No DJ accounts yet. Add them in the DJs tab.
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="ev-client">Client Name (optional)</Label>
              <Input
                id="ev-client"
                data-testid="event-client-input"
                value={form.client_name}
                onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                placeholder="e.g. The Patels"
                className={inputCls}
              />
            </div>
          </div>

          {editing && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 pt-2 border-t border-white/5">
              <div className="space-y-2">
                <Label>Request Mode</Label>
                <Select
                  value={form.mode}
                  onValueChange={(v) => setForm({ ...form, mode: v })}
                >
                  <SelectTrigger data-testid="event-mode-select" className={inputCls}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal" data-testid="event-mode-normal">Normal Mode</SelectItem>
                    <SelectItem value="approval" data-testid="event-mode-approval">Approval Mode</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ev-max">Max Requests per Guest (0 = unlimited)</Label>
                <Input
                  id="ev-max"
                  type="number"
                  min="0"
                  data-testid="event-max-input"
                  value={form.max_requests_per_guest}
                  onChange={(e) => setForm({ ...form, max_requests_per_guest: e.target.value })}
                  className={inputCls}
                />
              </div>
              <div className="space-y-2">
                <Label>Guest Messages</Label>
                <button
                  type="button"
                  data-testid="event-messages-toggle"
                  onClick={() => setForm({ ...form, allow_messages: !form.allow_messages })}
                  className={`w-full h-11 rounded-md border text-xs tracking-[0.2em] uppercase transition-colors duration-300 ${
                    form.allow_messages
                      ? "border-primary/50 text-primary"
                      : "border-white/15 text-muted-foreground"
                  }`}
                >
                  {form.allow_messages ? "Allowed" : "Disabled"}
                </button>
              </div>
            </div>
          )}

          <button
            type="submit"
            data-testid="event-save-btn"
            disabled={saving}
            className="rounded-full bg-primary text-primary-foreground px-8 py-3.5 text-xs font-medium tracking-[0.2em] uppercase transition-[transform,opacity] duration-300 hover:scale-105 disabled:opacity-60 flex items-center gap-2"
          >
            {saving && <Loader2 size={14} className="animate-spin" />}
            {editing ? "Save Changes" : "Create Event"}
          </button>
        </form>
      )}

      {events === null ? (
        <div className="flex justify-center py-16">
          <Loader2 size={28} className="animate-spin text-primary" />
        </div>
      ) : visible.length === 0 ? (
        <p data-testid="events-empty" className="text-center text-muted-foreground py-16 font-serif text-2xl">
          No {filter} events.
        </p>
      ) : (
        <div className="space-y-4" data-testid="events-list">
          {visible.map((e) => (
            <article key={e.id} data-testid={`event-card-${e.id}`} className="glass rounded-2xl p-6 space-y-4">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h3 className="font-serif text-2xl">{e.name}</h3>
                  <p className="text-[11px] tracking-[0.25em] uppercase text-muted-foreground mt-1">
                    {e.event_type} · {e.date}
                    {e.start_time ? ` ${e.start_time}` : ""}
                    {e.end_time ? `–${e.end_time}` : ""}
                    {e.dj_name ? ` · DJ ${e.dj_name}` : ""}
                    {e.client_name ? ` · ${e.client_name}` : ""}
                  </p>
                  <p className="mt-2 text-xs">
                    <span className="text-primary tracking-[0.2em]">CODE {e.code}</span>
                    <span className="text-muted-foreground/60 ml-3">
                      {window.location.origin}/request/{e.code}
                    </span>
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    data-testid={`event-requests-toggle-${e.id}`}
                    onClick={() =>
                      patch(e.id, { requests_enabled: !e.requests_enabled },
                        e.requests_enabled ? "Requests turned off." : "Requests are live!")
                    }
                    className={`rounded-full px-4 py-2 text-[10px] tracking-[0.2em] uppercase border flex items-center gap-1.5 transition-colors duration-300 ${
                      e.requests_enabled
                        ? "border-green-500/40 text-green-400"
                        : "border-white/15 text-muted-foreground"
                    }`}
                  >
                    <Power size={12} /> {e.requests_enabled ? "Requests On" : "Requests Off"}
                  </button>
                  <button
                    data-testid={`event-mode-toggle-${e.id}`}
                    onClick={() =>
                      patch(e.id, { mode: e.mode === "approval" ? "normal" : "approval" },
                        `Mode: ${e.mode === "approval" ? "Normal" : "Approval"}`)
                    }
                    className="rounded-full px-4 py-2 text-[10px] tracking-[0.2em] uppercase border border-white/15 text-muted-foreground hover:border-primary/50 hover:text-primary transition-colors duration-300"
                  >
                    {e.mode === "approval" ? "Approval Mode" : "Normal Mode"}
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 border-t border-white/5 pt-4">
                <button
                  data-testid={`event-qr-view-${e.id}`}
                  onClick={() => setQrEvent(e)}
                  className="rounded-full border border-primary/40 text-primary px-4 py-2 text-[10px] tracking-[0.2em] uppercase flex items-center gap-1.5 hover:bg-primary hover:text-primary-foreground transition-colors duration-300"
                >
                  <QrCode size={12} /> View QR
                </button>
                <a
                  href={`${API}/public/event/${e.code}/qrcode.png`}
                  download={`dream-request-${e.code}.png`}
                  data-testid={`event-qr-download-${e.id}`}
                  className="rounded-full border border-white/15 text-muted-foreground px-4 py-2 text-[10px] tracking-[0.2em] uppercase hover:border-primary/50 hover:text-primary transition-colors duration-300"
                >
                  Download QR
                </a>
                <button
                  data-testid={`event-copy-link-${e.id}`}
                  onClick={() => copyLink(e.code)}
                  className="rounded-full border border-white/15 text-muted-foreground px-4 py-2 text-[10px] tracking-[0.2em] uppercase flex items-center gap-1.5 hover:border-primary/50 hover:text-primary transition-colors duration-300"
                >
                  <Copy size={12} /> Copy Link
                </button>
                <a
                  href={`/request/${e.code}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`event-guest-page-${e.id}`}
                  className="rounded-full border border-white/15 text-muted-foreground px-4 py-2 text-[10px] tracking-[0.2em] uppercase flex items-center gap-1.5 hover:border-primary/50 hover:text-primary transition-colors duration-300"
                >
                  <ExternalLink size={12} /> Guest Page
                </a>
                <Link
                  to="/dj"
                  data-testid={`event-dj-dash-${e.id}`}
                  className="rounded-full border border-white/15 text-muted-foreground px-4 py-2 text-[10px] tracking-[0.2em] uppercase flex items-center gap-1.5 hover:border-primary/50 hover:text-primary transition-colors duration-300"
                >
                  <Radio size={12} /> DJ Dashboard
                </Link>
                <button
                  data-testid={`event-edit-${e.id}`}
                  onClick={() => startEdit(e)}
                  className="rounded-full border border-white/15 text-muted-foreground px-4 py-2 text-[10px] tracking-[0.2em] uppercase flex items-center gap-1.5 hover:border-primary/50 hover:text-primary transition-colors duration-300"
                >
                  <Pencil size={12} /> Edit
                </button>
                {!e.archived && (
                  <button
                    data-testid={`event-archive-${e.id}`}
                    onClick={() => patch(e.id, { archived: true }, "Event archived.")}
                    className="rounded-full border border-white/15 text-muted-foreground px-4 py-2 text-[10px] tracking-[0.2em] uppercase flex items-center gap-1.5 hover:border-accent hover:text-accent transition-colors duration-300"
                  >
                    <Archive size={12} /> Archive
                  </button>
                )}
                <button
                  data-testid={`event-delete-${e.id}`}
                  onClick={() => remove(e.id)}
                  className="rounded-full border border-white/15 text-muted-foreground px-4 py-2 text-[10px] tracking-[0.2em] uppercase flex items-center gap-1.5 hover:border-accent hover:text-accent transition-colors duration-300"
                >
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      {qrEvent && (
        <div
          data-testid="qr-modal"
          className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-md flex items-center justify-center px-6"
          onClick={() => setQrEvent(null)}
        >
          <div
            className="glass rounded-3xl p-8 max-w-sm w-full text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <Overline>{qrEvent.name}</Overline>
            <img
              src={`${API}/public/event/${qrEvent.code}/qrcode.png`}
              alt={`QR code for ${qrEvent.name}`}
              data-testid="qr-image"
              className="w-full rounded-2xl mt-6 bg-white p-4"
            />
            <p className="text-xs tracking-[0.25em] text-primary mt-4">CODE {qrEvent.code}</p>
            <p className="text-[11px] text-muted-foreground mt-1 break-all">
              {window.location.origin}/request/{qrEvent.code}
            </p>
            <div className="flex gap-3 mt-6">
              <a
                href={`${API}/public/event/${qrEvent.code}/qrcode.png`}
                download={`dream-request-${qrEvent.code}.png`}
                data-testid="qr-download-btn"
                className="flex-1 rounded-full bg-primary text-primary-foreground py-3 text-[11px] tracking-[0.2em] uppercase"
              >
                Download PNG
              </a>
              <button
                data-testid="qr-close-btn"
                onClick={() => setQrEvent(null)}
                className="flex-1 rounded-full border border-white/20 py-3 text-[11px] tracking-[0.2em] uppercase hover:border-primary hover:text-primary transition-colors duration-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
